import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ResearchOverlap, ResearchPersonMatch } from '../../models/research.models';

/** Font Awesome icon per overlap kind (Task 17 brief, refinement R19). `coauthor` → pen (direct co-authorship), `org` → building (shared affiliation), `session` → chalkboard (co-convened meeting session), `mutual` → people-arrows (shared third-party co-authors). */
const OVERLAP_ICON: Readonly<Record<string, string>> = {
    coauthor: 'fa-pen',
    org: 'fa-building',
    session: 'fa-chalkboard',
    mutual: 'fa-people-arrows',
};

const OVERLAP_TITLE: Readonly<Record<string, string>> = {
    coauthor: 'Co-authorship',
    org: 'Shared affiliation',
    session: 'Co-convened session',
    mutual: 'Mutual co-authors',
};

/** "JC" from "Dr. Jordan Chen" — first letter of the first two whitespace-separated tokens, uppercased. Mirrors AGUSidebarIdentity's own initials derivation (app-sidebar.component.ts) so the two avatar circles in the surface agree on the algorithm. */
export function PersonInitials(name: string): string {
    const initials = name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? '')
        .join('');
    return initials || '?';
}

/** "Research Scientist · NOAA" / just the job title / just the organization / "Not stated" when both are null. */
export function PersonRoleLine(person: Pick<ResearchPersonMatch, 'jobTitle' | 'organization'>): string {
    const parts = [person.jobTitle, person.organization].filter((v): v is string => Boolean(v && v.trim()));
    return parts.length > 0 ? parts.join(' · ') : 'Not stated';
}

/**
 * The mutual-coauthor OVERLAP's label is server-formatted (overlaps.ts
 * `DescribeOverlaps`) as `"${MutualCount} co-author(s) in common"` — always a
 * leading integer. Parsed here rather than adding a second wire field for the
 * same number, since the label is already the source of truth for it.
 */
export function MutualCoauthorCount(overlaps: readonly ResearchOverlap[]): number {
    const mutual = overlaps.find((o) => o.kind === 'mutual');
    if (!mutual) return 0;
    const match = /^(\d+)/.exec(mutual.label);
    return match ? Number(match[1]) : 0;
}

/**
 * The card's one status chip: "Co-author" when the member and this person
 * have directly co-authored something together, else "N mutual co-authors"
 * when they share third-party co-authors, else no chip at all.
 */
export function PersonTagChip(overlaps: readonly ResearchOverlap[]): string | null {
    if (overlaps.some((o) => o.kind === 'coauthor')) return 'Co-author';
    const n = MutualCoauthorCount(overlaps);
    if (n > 0) return `${n} mutual co-author${n === 1 ? '' : 's'}`;
    return null;
}

/** One row in the "What you share" box: an icon + the overlap's own server-formatted label. */
export interface PersonWhyItem {
    icon: string;
    title: string;
    label: string;
}

/** The card's top 3 overlaps (the detail view asks for `Infinity`), most-relevant-first as the server already ordered them (coauthor/mutual before org/session — see overlaps.ts `DescribeOverlaps`). */
export function PersonWhyItems(overlaps: readonly ResearchOverlap[], limit = 3): readonly PersonWhyItem[] {
    return overlaps.slice(0, limit).map((o) => ({
        icon: OVERLAP_ICON[o.kind] ?? 'fa-circle-info',
        title: OVERLAP_TITLE[o.kind] ?? o.kind,
        label: o.label,
    }));
}

/**
 * A collaborator result card: wraps `agu-item-card` with the person-specific
 * identity block (avatar + role/location), the "What you share" overlap box,
 * an ORCID/LinkedIn links row, and View details / AGU profile actions.
 * `Save`/`Pin`/`Dismiss`/`Open` relay the underlying item-card events under
 * person-domain names, matching `GrantCardComponent`'s shape (Task 16).
 */
@Component({
    standalone: false,
    selector: 'ra-person-card',
    templateUrl: './person-card.component.html',
    styleUrl: './person-card.component.scss',
})
export class PersonCardComponent {
    @Input({ required: true }) Person!: ResearchPersonMatch;
    @Input() Recommended = false;
    /** Every card carries the pin in the mockup, on both tabs (parity/collaborators.md §4.7 row 7); the Saved page opts out. */
    /** The title's heading level: 2 on the Saved board (P/for-you.html:947), 3 in the finders. */
    @Input() TitleLevel: 2 | 3 = 3;
    @Input() ShowPin = true;
    @Input() ShowDismiss = true;

    @Output() Save = new EventEmitter<boolean>();
    @Output() Pin = new EventEmitter<boolean>();
    @Output() Dismiss = new EventEmitter<void>();
    @Output() Open = new EventEmitter<void>();

    get Initials(): string {
        return PersonInitials(this.Person.name);
    }

    get RoleLine(): string {
        return PersonRoleLine(this.Person);
    }

    /** The one network chip the mockup's `.degpill` carries — null (no chip) rather than the mockup's asserted "No mutual co-authors", which we cannot stand behind without a complete co-authorship graph (parity/collaborators.md §6). */
    get NetworkChip(): string | null {
        return PersonTagChip(this.Person.overlaps);
    }

    /** Every overlap we hold, so the why box can offer the mockup's `See all {N} shared` link when it shows only the top three. */
    get SharedTotal(): number {
        return this.Person.overlaps.length;
    }

    get WhyItems(): readonly PersonWhyItem[] {
        return PersonWhyItems(this.Person.overlaps);
    }
}
